import { Form } from "antd";
import { useWatch } from "antd/es/form/Form";
import { useCallback, useMemo, useState } from "react";
import { Market, Schedule } from "tm-shared/types";
import { matchesQuery } from "tm-shared/utils";

import { useAppSelector } from "@/hooks/redux";
import { useDebounce } from "@/hooks/useDebounce";
import { useMultiTap } from "@/hooks/useMultiTap";

import { FiltersPanelValues } from "../components/FiltersPanel";

interface UseScheduleSelectionArgs {
    markets: Record<string, Market>;
}

export const useScheduleSelection = ({ markets }: UseScheduleSelectionArgs) => {
    const { currentScheduleList } = useAppSelector((s) => s.sv);

    const [filtersForm] = Form.useForm<FiltersPanelValues>();
    const watchedFilters = useWatch([], filtersForm);

    const searchValue = watchedFilters?.search || "";
    const debouncedSearch = useDebounce(searchValue, 300);

    const [multiSelectMode, setMultiSelectMode] = useState(false);
    const [selectedSchedules, setSelectedSchedules] = useState<Schedule[]>([]);

    const enterMultiSelectMode = useMultiTap(() => setMultiSelectMode(true));

    const exitMultiSelectMode = useCallback(() => {
        setMultiSelectMode(false);
        setSelectedSchedules([]);
    }, []);

    const sortedAndFilteredSchedules = useMemo(() => {
        const { day = "0" } = watchedFilters || {};
        const searchLower = debouncedSearch.trim().toLowerCase();

        setSelectedSchedules([]);

        return [...currentScheduleList]
            .filter((schedule) => {
                if (day !== "0") {
                    const scheduleDays = schedule.days.map((d) => Number(d));
                    if (!scheduleDays.includes(Number(day))) return false;
                }

                if (searchLower) {
                    const currentMarket = markets[schedule.marketId];

                    if (!currentMarket) return;

                    const matches = matchesQuery(currentMarket, searchLower, [
                        (m) => m?.address?.name,
                        (m) => m?.contragent?.name,
                        (m) => m?.name,
                    ]);

                    if (!matches) return false;
                }

                return true;
            })
            .sort((a, b) => Number(a.sortOrder) - Number(b.sortOrder));
    }, [watchedFilters, debouncedSearch, currentScheduleList, markets]);

    const handleSelectSchedule = useCallback(
        (item: Schedule) => {
            if (!multiSelectMode) return;

            setSelectedSchedules((prev) => {
                const exists = prev.some((p) => p.marketId === item.marketId);
                return exists
                    ? prev.filter((p) => p.marketId !== item.marketId)
                    : [...prev, item];
            });
        },
        [multiSelectMode],
    );

    const handleSelectAll = useCallback(() => {
        setSelectedSchedules([...sortedAndFilteredSchedules]);
    }, [sortedAndFilteredSchedules]);

    const handleClearSelection = useCallback(
        () => setSelectedSchedules([]),
        [],
    );

    return {
        filtersForm,
        watchedFilters,

        multiSelectMode,
        selectedSchedules,
        sortedAndFilteredSchedules,

        enterMultiSelectMode,
        exitMultiSelectMode,

        handleSelectSchedule,
        handleSelectAll,
        handleClearSelection,
    };
};
